import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  message,
  Modal,
  notification,
  Popover,
  Row,
  Select,
  Space,
  Tabs,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import useIsMobile from "../../../../hooks/useIsMobile";
import Loading from "../../../../components/shared-components/Loading";
import QuantityControl from "../../../../components/shared-components/QuantityControl";
import BarcodeScanner from "../../../../components/shared-components/BarcodeScanner";
import { deleteDoc, doc, getDoc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import {
  FIRESTORE_CRATE_STOCKS_TABLE,
  FIRESTORE_ORDERPICKINGS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { firestore } from "../../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import {
  ADDITIONAL_DELIVERY_LABEL,
  FRESHING_BOX_DELIVERY_LABELS,
  ORDER_PICKING_STATUS_COMPLETED,
  ORDER_PICKING_STATUS_IN_PROGRESS,
} from "../../../../constants/OrderConstant";
import { generateAndEmailDocuments, sooCoolOrder, stockApiUrl } from "../../../../urls";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import axios from "axios";
import { isEqual } from "lodash";
import {
  calculateOrderUpdatePricing,
  handleOrderPackingStatusChange,
  handleSooCoolOrderChanges,
  prepareShoppingCartUpdate,
  sendOrderUpdateNotification,
} from "../../../../functions/orders";
import { applyCustomerPricingToProduct, getBundleItemsWithCustomerPricing } from "../../../../functions/pricing";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useLeads } from "../../../../data/hooks/firestore/useLeads";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useWebshopUsers } from "../../../../data/hooks/firestore/useWebshopUsers";
import { usePriceLists } from "../../../../data/hooks/firestore/usePriceLists";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import { CRATE_BARCODES, CRATES } from "../../../../constants/PackagingConstants";
import { useCrateStocks } from "../../../../data/hooks/firestore/useCrateStocks";
import { LOG_ACTION_RESTOCKED_UNPICK, LOG_ACTION_SOLD } from "../../../../constants/CratesConstants";
import ScanBundleItemsModal from "./modals/ScanBundleItemsModal";
import ProductDetailModal from "./modals/ProductDetailModal";
import {
  appendOrderPickingEvent,
  applySoldToCrate,
  extractItemMetadataFromEvents,
  getCrateEvents,
  getCrateStockItem,
  restockCrate,
  restoreCrateEventsFromLog,
  sanitizeCrateDocId,
} from "../../../../functions/crateStock";
import { omitBundlePackaging, updateProductCountedStock } from "../../../../functions/productStock";
import { sendOrderToFreshingBox } from "../../../../functions/api/freshingbox";
import { getPickingDate } from "../../../../functions/deliveryValidation";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const CRATE_STOCK_BARCODE_MAX_LENGTH = 99;
const LOCAL_STORAGE_CRATE_CODES_KEY = "orderPickingDetail.mockScanner.crateCodes";
const LOCAL_STORAGE_PRODUCT_CODES_KEY = "orderPickingDetail.mockScanner.productCodes";

const normalizeBarcode = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const digits = str.replace(/\D/g, "");
  // Some barcodes may be stored as numbers in Firestore (leading zeros lost). To avoid false mismatches,
  // compare using a normalized representation without leading zeros.
  return digits.replace(/^0+/, "");
};

const OrderPickingDetail = ({ companyFilter = "All", logoSrc, logoAlt }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const isMobile = useIsMobile();
  const [internalNotesForm] = Form.useForm();

  const { data: orders, loading: ordersLoading } = useOrders();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { leads, isLoading: leadsLoading } = useLeads();
  const { products, isLoading: productsLoading, getProductByBarcode } = useProducts();
  const { webshopUsers: users, isLoading: webshopUsersLoading } = useWebshopUsers();
  const { priceLists } = usePriceLists();
  const { getCountriesMap, getDeliveryLabelLeadDays } = useCountries();
  const { crateStocks, isLoading: crateStocksLoading } = useCrateStocks();
  const { uid, email } = useAuth();

  const normalizeScannedCrates = (crates = []) => {
    return (Array.isArray(crates) ? crates : [])
      .map((crate) => ({
        barcode: crate?.barcode || "",
        size: crate?.size || "",
        quantity: Number(crate?.quantity || 0),
      }))
      .sort((a, b) => {
        const keyA = `${a.barcode}-${a.size}`;
        const keyB = `${b.barcode}-${b.size}`;
        return keyA.localeCompare(keyB);
      });
  };

  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [mainOrder, setMainOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fulfillmentData, setFulfillmentData] = useState({});
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addProductModalVisible, setAddProductModalVisible] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [orderPickingDoc, setOrderPickingDoc] = useState(null);
  const [originalShoppingCart, setOriginalShoppingCart] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [remarksPopoverVisible, setRemarksPopoverVisible] = useState(false);
  const [notesPopoverVisible, setNotesPopoverVisible] = useState(false);
  const [notesPopoverAutoOpened, setNotesPopoverAutoOpened] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [crateQuantity, setCrateQuantity] = useState(1);
  const [scannedCrates, setScannedCrates] = useState([]); // Array of {barcode, quantity, size}
  const [cratesModalVisible, setCratesModalVisible] = useState(false);

  const [pickingCrate, setPickingCrate] = useState(null);
  const [pickingBaseFulfilled, setPickingBaseFulfilled] = useState(0);
  const [pickingScannedCount, setPickingScannedCount] = useState(0);
  const [awaitingCrateScan, setAwaitingCrateScan] = useState(false);
  const [emptiedCrates, setEmptiedCrates] = useState([]);
  const [pickedCrates, setPickedCrates] = useState([]);

  const [mockScannerOpen, setMockScannerOpen] = useState(false);
  const [mockCrateCodes, setMockCrateCodes] = useState([]);
  const [mockProductCodes, setMockProductCodes] = useState([]);
  const [mockCrateCodeInput, setMockCrateCodeInput] = useState("");
  const [mockProductCodeInput, setMockProductCodeInput] = useState("");

  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [barcodeScannerEnabled, setBarcodeScannerEnabled] = useState(() => {
    const saved = localStorage.getItem("barcodeScannerEnabled");
    return saved === "true";
  });

  const [scanBundleModalVisible, setScanBundleModalVisible] = useState(false);
  const [scannedBundleItems, setScannedBundleItems] = useState(null);

  // Ref to track unpicking operations in progress (prevents race conditions)
  const unpickingInProgressRef = useRef(new Set());

  const getPersistedCrateLinesForProduct = (productId) => {
    if (!productId) return [];
    const events = Array.isArray(orderPickingDoc?.cratePickEvents) ? orderPickingDoc.cratePickEvents : [];
    const soldEvents = events.filter((ev) => ev?.type === LOG_ACTION_SOLD && ev?.productId === productId);
    const aggregated = {};

    soldEvents.forEach((ev) => {
      const crateKey = String(ev?.crateId || ev?.crateDocId || "").trim();
      if (!crateKey) return;
      const qty = Number(ev?.remainingQuantity ?? ev?.quantity ?? 0);
      aggregated[crateKey] = (aggregated[crateKey] || 0) + qty;
    });

    return Object.entries(aggregated)
      .map(([crateId, quantity]) => ({ crateId, quantity: Number(quantity || 0) }))
      .filter((line) => line.quantity > 0)
      .sort((a, b) => String(a.crateId).localeCompare(String(b.crateId)));
  };

  useEffect(() => {
    try {
      const crateCodes = JSON.parse(localStorage.getItem(LOCAL_STORAGE_CRATE_CODES_KEY) || "[]");
      const productCodes = JSON.parse(localStorage.getItem(LOCAL_STORAGE_PRODUCT_CODES_KEY) || "[]");

      setMockCrateCodes(Array.isArray(crateCodes) ? crateCodes : []);
      setMockProductCodes(Array.isArray(productCodes) ? productCodes : []);
    } catch (e) {
      setMockCrateCodes([]);
      setMockProductCodes([]);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_CRATE_CODES_KEY, JSON.stringify(mockCrateCodes || []));
    } catch (e) {
      // ignore
    }
  }, [mockCrateCodes]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_PRODUCT_CODES_KEY, JSON.stringify(mockProductCodes || []));
    } catch (e) {
      // ignore
    }
  }, [mockProductCodes]);

  const simulateScan = (code) => {
    const str = String(code || "").trim();
    if (!str) return;

    const dispatchKey = (target, key) => {
      try {
        target.dispatchEvent(
          new KeyboardEvent("keydown", {
            key,
            bubbles: true,
          }),
        );
      } catch (e) {
        // ignore
      }
    };

    [...str].forEach((k) => {
      dispatchKey(document, k);
    });
    dispatchKey(document, "Enter");
  };

  const addMockCode = (type) => {
    const raw = type === "crate" ? mockCrateCodeInput : mockProductCodeInput;
    const code = String(raw || "").trim();
    if (!code) return;
    if (!/^[0-9]+$/.test(code)) {
      message.error(t("crate_stock.code_must_be_numeric"));
      return;
    }

    if (type === "crate") {
      if (code.length > CRATE_STOCK_BARCODE_MAX_LENGTH) {
        message.error(t("crate_stock.crate_code_max_n_digits", { digits: CRATE_STOCK_BARCODE_MAX_LENGTH }));
        return;
      }
      if (!mockCrateCodes.includes(code)) setMockCrateCodes((prev) => [...prev, code]);
      setMockCrateCodeInput("");
      return;
    }

    if (code.length < 8 || code.length > 13) {
      message.error(t("crate_stock.product_code_must_be_8_13_digits"));
      return;
    }
    if (!mockProductCodes.includes(code)) setMockProductCodes((prev) => [...prev, code]);
    setMockProductCodeInput("");
  };

  const handleCameraBarcodeScan = (scannedValue) => {
    if (scannedValue) {
      simulateScan(scannedValue);
      message.success(t("notifications.barcode_scanned"));
    }
  };

  const removeMockCode = (type, code) => {
    if (type === "crate") {
      setMockCrateCodes((prev) => (prev || []).filter((c) => c !== code));
      return;
    }
    setMockProductCodes((prev) => (prev || []).filter((c) => c !== code));
  };

  const hasInternalNotes = Boolean(order?.internalNotes?.text || orderPickingDoc?.internalNotes?.text);

  useEffect(() => {
    setNotesPopoverAutoOpened(false);
    setNotesPopoverVisible(false);
  }, [orderId]);

  useEffect(() => {
    if (!hasInternalNotes) return;
    if (notesPopoverAutoOpened) return;

    setNotesPopoverVisible(true);
    setNotesPopoverAutoOpened(true);
  }, [hasInternalNotes, notesPopoverAutoOpened]);
  const [tempScannedCrates, setTempScannedCrates] = useState([]);
  const [tempFulfilled, setTempFulfilled] = useState(0);
  const [finishLoading, setFinishLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [addShippingCost, setAddShippingCost] = useState(true);

  // Find the current order and create/load orderPicking document
  useEffect(() => {
    if (
      orders &&
      customers &&
      leads &&
      products &&
      orderId &&
      uid &&
      !ordersLoading &&
      !customersLoading &&
      !leadsLoading &&
      !productsLoading &&
      !webshopUsersLoading &&
      !isInitialized // Only run once after initial load
    ) {
      const currentOrder = orders.find((o) => o.id === orderId);
      if (currentOrder) {
        setOrder(currentOrder);
        setOriginalShoppingCart([...currentOrder.userShoppingCart]);
        setDeliveryDate(dayjs(currentOrder?.farmorderDeliveryDate).format("DD-MM-YYYY"));

        // Initialize shipping cost toggle based on whether order actually has shipping costs
        // Check if shippingCost is explicitly disabled OR if shipping cost amount is 0
        const hasShippingCosts =
          currentOrder.shippingCostDisabled === true ? false : parseFloat(currentOrder.shippingCost || 0) > 0;
        setAddShippingCost(hasShippingCosts);

        const orderCustomer = customers.find((c) => c.id === currentOrder.farmorderCustomerId);
        setCustomer(orderCustomer);

        // If this is an additional delivery, fetch the main order
        if (currentOrder.deliveryLabel === ADDITIONAL_DELIVERY_LABEL && currentOrder.mainOrderId) {
          const mainOrderData = orders.find((o) => o.id === currentOrder.mainOrderId);
          setMainOrder(mainOrderData);
        }

        // Create orderPicking document immediately
        createOrderPickingDocument(currentOrder, orderCustomer);
        setIsInitialized(true);
      }
      setIsLoading(false);
    }
  }, [
    orders,
    customers,
    leads,
    products,
    orderId,
    uid,
    ordersLoading,
    customersLoading,
    leadsLoading,
    productsLoading,
    webshopUsersLoading,
    isInitialized,
  ]);

  useEffect(() => {
    if (!orderId || !uid) return;

    const docRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_ORDERPICKINGS_TABLE,
      orderId,
    );

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (!data) return;
        setOrderPickingDoc(data);
      },
      (error) => {
        console.error("OrderPicking onSnapshot error:", error);
      },
    );

    return () => unsubscribe();
  }, [orderId, uid]);

  useEffect(() => {
    if (notesModalVisible || scanBundleModalVisible) return;
    let barcode = "";

    const handleScannerInput = (e) => {
      if (e.code?.includes?.("Digit") || /^[0-9]$/.test(e.key)) {
        barcode += e.key;
      }
      if (!!barcode.length && e.key === "Enter") {
        if (barcode.length > CRATE_STOCK_BARCODE_MAX_LENGTH) {
          message.error(t("notifications.error_invalid_barcode"));
        }
        handleBarcodeScan(barcode).then(() => (barcode = ""));
      }
    };

    document.addEventListener("keydown", handleScannerInput);
    return () => {
      document.removeEventListener("keydown", handleScannerInput);
    };
  });

  useEffect(() => {
    if (scannedCrates.length > 0) {
      const totalCrates = scannedCrates.reduce((sum, crate) => sum + crate.quantity, 0);
      setCrateQuantity(totalCrates);
    } else if (!scannedCrates.length && !crateQuantity) {
      setCrateQuantity(1); // Default to 1
    }
  }, [scannedCrates]);

  // Helper function to convert fulfillmentData to shopping cart
  const convertFulfillmentToShoppingCart = () => {
    const newShoppingCart = [];

    Object.entries(fulfillmentData).forEach(([productId, data]) => {
      // Include product if:
      // 1. It has fulfilled quantity > 0, OR
      // 2. It was originally in the order with itemCount: 0 (customer-driven removal)
      const originalProduct = originalShoppingCart.find((p) => p.productId === productId);
      const shouldInclude = data.fulfilled > 0 || (originalProduct && originalProduct.itemCount === 0);

      if (shouldInclude) {
        const product = products?.find((p) => p.productId === productId);
        const originalProduct = originalShoppingCart.find((p) => p.productId === productId);
        // Also check current order.userShoppingCart for products added during picking
        const currentProduct = order?.userShoppingCart?.find((p) => p.productId === productId);

        if (product) {
          // Use price from original order if it exists, otherwise calculate
          const finalPrice =
            originalProduct?.productPrice ||
            currentProduct?.productPrice ||
            applyCustomerPricingToProduct(product, customer, priceLists, getCountriesMap());

          const cartItem = {
            productId: productId,
            productName: product.productName,
            itemCount: data.fulfilled,
            productPrice: finalPrice,
            externalId: product.exactId?.toString() || "",
            orderable: true,
            productCategory: product.category?.en || "",
            productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
            productBarcode: product.barcode || "",
          };

          // Preserve changeRequestedBy/changeReason if it existed on original product
          if (originalProduct?.changeRequestedBy) {
            cartItem.changeRequestedBy = originalProduct.changeRequestedBy;
            cartItem.changeReason = originalProduct.changeReason;
          }

          // Preserve bundle-specific fields if this is a bundle
          // Check both originalProduct and currentProduct (for bundles added during picking)
          const bundleSource = originalProduct?.isBundle
            ? originalProduct
            : currentProduct?.isBundle
              ? currentProduct
              : null;
          if (bundleSource?.isBundle) {
            cartItem.isBundle = bundleSource.isBundle;
            cartItem.bundleItems = bundleSource.bundleItems;
            cartItem.packagingMaterial = bundleSource.packagingMaterial;
            cartItem.hasCustomPrice = bundleSource.hasCustomPrice;
          }

          newShoppingCart.push(cartItem);
        }
      }
    });

    return newShoppingCart;
  };

  const createOrderPickingDocument = async (currentOrder, orderCustomer) => {
    try {
      const docRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERPICKINGS_TABLE,
        orderId,
      );

      const existingDoc = await getDoc(docRef);

      // Acquisition orders have no customer; their country comes from the lead.
      const pickingCountry =
        currentOrder?.type === "acquisition"
          ? leads.find((l) => l.leadId === currentOrder.leadId)?.country
          : orderCustomer?.country;

      if (existingDoc.exists) {
        const existingData = existingDoc.data();

        // Check if existingData is valid before accessing properties
        if (!existingData) {
          console.warn("Document exists but data is undefined, creating new document");
          // Fall through to create new document instead of throwing error
        } else {
          // Set isPaused to false when resuming. Backfill pickingDate for documents
          // created before it existed, so the picking overview can find them again.
          const resumeUpdate = {
            isPaused: false,
            updatedAt: dayjs().unix(),
          };
          if (!existingData.pickingDate) {
            // For ADD orders, use delivery label from main order
            let deliveryLabel = currentOrder.deliveryLabel;
            if (deliveryLabel === "ADD" && currentOrder.mainOrderId) {
              const mainOrderData = orders.find((o) => o.id === currentOrder.mainOrderId);
              if (mainOrderData) {
                deliveryLabel = mainOrderData.deliveryLabel;
              }
            }

            const leadDays = getDeliveryLabelLeadDays(pickingCountry, deliveryLabel);
            resumeUpdate.pickingDate = getPickingDate(currentOrder.farmorderDeliveryDate, leadDays).format(
              "YYYY-MM-DD",
            );
          }
          await updateDoc(docRef, resumeUpdate);

          setOrderPickingDoc(existingData);

          // If status is completed, treat as packed
          if (existingData.status === ORDER_PICKING_STATUS_COMPLETED) {
            setOrder((prev) => ({ ...prev, packed: true }));
          }

          // Set original cart from saved data (fallback to current order cart)
          const lockedOriginalCart = existingData.originalShoppingCart || currentOrder.userShoppingCart || [];
          setOriginalShoppingCart(lockedOriginalCart);

          // Load existing fulfillment data and reconstruct shopping cart ONLY from pickedItems
          const existingFulfillment = {};
          const reconstructedShoppingCart = [];

          if (existingData.pickedItems) {
            existingData.pickedItems.forEach((item) => {
              existingFulfillment[item.productId] = {
                requested: item.requestedQuantity,
                fulfilled: item.pickedQuantity,
                ready: item.isReady,
              };

              // Reconstruct shopping cart item from pickedItems
              const product = products?.find((p) => p.productId === item.productId);
              if (product) {
                const lockedCartItem = lockedOriginalCart.find((p) => p.productId === item.productId);
                reconstructedShoppingCart.push({
                  productId: item.productId,
                  productName: product.productName,
                  itemCount: item.requestedQuantity,
                  productPrice: lockedCartItem?.productPrice || "0",
                });
              }
            });
          }

          // Update order with reconstructed shopping cart from pickedItems ONLY
          setOrder((prev) => ({
            ...prev,
            userShoppingCart: reconstructedShoppingCart,
            internalNotes: existingData.internalNotes,
            orderCommentsProcessed: existingData.orderCommentsProcessed,
          }));
          setCrateQuantity(existingData.crates || 1);
          setScannedCrates(existingData.scannedCrates || []);
          setFulfillmentData(existingFulfillment);
          return; // Exit early if we successfully processed existing data
        }
      }

      // Create new orderPicking document (either no document exists or data was invalid)
      {
        // Create new orderPicking document with all original products
        const initialPickedItems = currentOrder.userShoppingCart.map((item) => ({
          productId: item.productId,
          requestedQuantity: item.itemCount,
          pickedQuantity: 0,
          isReady: item.itemCount === 0,
        }));

        // For ADD orders, use delivery label from main order
        let deliveryLabel = currentOrder.deliveryLabel;
        if (deliveryLabel === "ADD" && currentOrder.mainOrderId) {
          const mainOrderData = orders.find((o) => o.id === currentOrder.mainOrderId);
          if (mainOrderData) {
            deliveryLabel = mainOrderData.deliveryLabel;
          }
        }

        const leadDays = getDeliveryLabelLeadDays(pickingCountry, deliveryLabel);
        const pickingDate = getPickingDate(currentOrder.farmorderDeliveryDate, leadDays).format("YYYY-MM-DD");

        const orderPickingData = {
          farmorderId: currentOrder.farmorderId,
          deliveryDate: currentOrder.farmorderDeliveryDate,
          pickingDate,
          status: ORDER_PICKING_STATUS_IN_PROGRESS,
          userId: uid,
          userEmail: email,
          startedAt: dayjs().unix(),
          pickedItems: initialPickedItems,
          originalShoppingCart: currentOrder.userShoppingCart,
          internalNotes: currentOrder.internalNotes || null,
          crates: currentOrder.crates || 1,
          scannedCrates: currentOrder.scannedCrates || [],
          isPaused: false,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        };

        await setDoc(docRef, orderPickingData);
        setOrderPickingDoc(orderPickingData);
        setCrateQuantity(currentOrder.crates || 1);
        setScannedCrates(currentOrder.scannedCrates || []);
        initializeNewFulfillmentData(currentOrder);
      }
    } catch (error) {
      console.error("Error creating/loading order picking document:", error);
      message.error(t("error_loading_picking_progress"));
      initializeNewFulfillmentData(currentOrder);
    }
  };

  const handleSendToSooCool = async (orderData, action) => {
    const token = await getFirebaseIDToken();

    if (action === "create") {
      const response = await axios.post(sooCoolOrder, orderData, {
        headers: { Authorization: token },
      });

      if (response.status === 201 && response.data?.sooCoolId) {
        // Update order with sooCoolId
        await updateDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, orderId),
          { sooCoolId: response.data.sooCoolId, updatedAt: dayjs().unix(), updatedBy: email },
        );
      }
    } else if (action === "update") {
      if (orderData.sooCoolId) {
        console.log("[SooCool debug] handleSendToSooCool: PUT update", { sooCoolId: orderData.sooCoolId });
        const response = await axios.put(`${sooCoolOrder}/${orderData.sooCoolId}`, orderData, {
          headers: { Authorization: token },
        });
        console.log("[SooCool debug] handleSendToSooCool: PUT response", response?.data);
        return response;
      } else {
        console.log("[SooCool debug] handleSendToSooCool: action=update but orderData.sooCoolId is missing", orderData);
      }
    }
  };

  const initializeNewFulfillmentData = (currentOrder) => {
    const initialFulfillment = {};
    currentOrder.userShoppingCart?.forEach((item) => {
      initialFulfillment[item.productId] = {
        requested: item.itemCount,
        fulfilled: 0,
        ready: item.itemCount === 0,
      };
    });
    setFulfillmentData(initialFulfillment);
  };

  // Redirect if not mobile
  if (!isMobile) {
    navigate("/cms/orders/chefs-farm");
    return null;
  }

  const handleExitWithoutFinishing = () => {
    // If order is already packed, just go back without showing dialog
    if (order.packed) {
      navigate(-1);
      return;
    }

    // Show modal for unpacked orders
    setExitModalVisible(true);
  };

  const handleSaveForLater = async () => {
    await saveOrderPickingProgress(ORDER_PICKING_STATUS_IN_PROGRESS);
    navigate(-1);
  };

  const handleDiscardProgress = async () => {
    setIsDiscarding(true);
    await discardOrderPickingProgress();
    navigate(-1);
  };

  const handleOrderPickingFinish = async () => {
    setFinishLoading(true);
    try {
      // Fetch latest order state from Firestore to check if already packed
      const orderDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERS_TABLE,
        orderId,
      );
      const latestOrderSnap = await getDoc(orderDocRef);
      const latestOrder = latestOrderSnap.data();

      if (latestOrder?.packed === true) {
        Modal.error({
          title: t("order_already_packed"),
          content: t("order_already_packed_unpack_first"),
        });
        setFinishLoading(false);
        return;
      }

      // Check if all items are ready
      const unfinishedItems = Object.entries(fulfillmentData).filter(([productId, data]) => !data.ready);

      if (unfinishedItems.length > 0) {
        Modal.warning({
          title: t("unfulfilled_items"),
          content: t("not_all_items_fulfilled_warning"),
        });
        return;
      }

      // Check if comments haven't been processed
      if (order.farmorderComments && !orderPickingDoc?.orderCommentsProcessed && !order.orderCommentsProcessed) {
        confirm({
          title: t("unprocessed_comment"),
          content: t("order_has_unprocessed_comment_finish_anyway"),
          okText: t("yes_finish_anyway"),
          cancelText: t("no_go_back"),
          onOk: async () => {
            await finishOrderWithUpdates();
          },
          onCancel: () => {
            // User cancelled, do nothing
          },
        });
        return;
      }

      await finishOrderWithUpdates();
    } catch (error) {
      console.error("Error in handleOrderPickingFinish:", error);
      message.error(t("error_completing_order"));
    } finally {
      setFinishLoading(false);
    }
  };

  const finishOrderWithUpdates = async () => {
    try {
      // Convert fulfillment data to new shopping cart
      const newShoppingCart = convertFulfillmentToShoppingCart();

      // Check if shopping cart actually changed by comparing quantities
      const actuallyChanged = hasShoppingCartActuallyChanged(originalShoppingCart, newShoppingCart);

      // Get discount code data from order
      const discountCodeData = {
        applied: order?.discountCodeApplied || false,
        amount: order?.discountCodeAmount || 0,
        percentage: order?.discountCodePercentage || 0,
      };

      // Prepare shopping cart update - but only if actually changed
      const cartUpdateResult = actuallyChanged
        ? prepareShoppingCartUpdate(order, newShoppingCart, email, false)
        : { hasChanges: false, shoppingCartChanged: false };

      const shoppingCartChanged = cartUpdateResult.shoppingCartChanged;

      const newCrates = crateQuantity || 1;
      const cratesChanged = (order.crates || 1) !== newCrates;
      const scannedCratesChanged = !isEqual(
        normalizeScannedCrates(order.scannedCrates),
        normalizeScannedCrates(scannedCrates),
      );

      // Base update object
      const updateObject = {
        crates: newCrates,
        scannedCrates: scannedCrates || [],
      };

      // Add cart update data if changed
      if (cartUpdateResult.hasChanges) {
        Object.assign(updateObject, cartUpdateResult.updateObject);
      }

      // Add comment processing if applicable
      if (orderPickingDoc?.orderCommentsProcessed || order.orderCommentsProcessed) {
        updateObject.orderCommentsProcessed = true;
      }

      // Add internal notes processing if applicable
      if (orderPickingDoc?.internalNotes || order.internalNotes) {
        updateObject.internalNotes = orderPickingDoc?.internalNotes || order.internalNotes;
      }

      // Calculate new pricing if cart changed
      if (shoppingCartChanged) {
        const pricingUpdate = calculateOrderUpdatePricing(
          newShoppingCart,
          customer,
          order.deliveryLabel,
          addShippingCost, // Use the toggle state instead of hardcoded true
          discountCodeData,
          products,
          order,
          null, // no delivery time charge change
          priceLists,
          getCountriesMap(),
        );

        Object.assign(updateObject, pricingUpdate);
      }

      // Compute pickedCratesInfo: per product (or bundle), which crate IDs were used and how much.
      // For bundle sub-items (ev.bundleProductId set), group under the bundle's productId
      // and record the sub-item productId so the frontend can show the breakdown.
      const cratePickEvents = Array.isArray(orderPickingDoc?.cratePickEvents) ? orderPickingDoc.cratePickEvents : [];
      if (cratePickEvents.length > 0) {
        const pickedCratesMap = {};
        cratePickEvents
          .filter((ev) => ev?.type === LOG_ACTION_SOLD)
          .forEach((ev) => {
            const groupKey = ev?.bundleProductId || ev?.productId;
            const subItemProductId = ev?.bundleProductId ? ev?.productId : null;
            if (!groupKey) return;
            const crateKey = String(ev?.crateId || ev?.crateDocId || "").trim();
            if (!crateKey) return;
            const qty = Number(ev?.remainingQuantity ?? ev?.quantity ?? 0);

            // For bundle sub-items, key by crateId + subItemProductId to keep separate lines per sub-item
            const mapKey = subItemProductId ? `${crateKey}::${subItemProductId}` : crateKey;

            if (!pickedCratesMap[groupKey]) pickedCratesMap[groupKey] = {};
            if (!pickedCratesMap[groupKey][mapKey]) {
              pickedCratesMap[groupKey][mapKey] = {
                crateId: crateKey,
                quantity: 0,
                packageDateUnix: ev?.packageDateUnix || null,
                ...(subItemProductId ? { subItemProductId } : {}),
              };
            }
            pickedCratesMap[groupKey][mapKey].quantity += qty;
          });

        const pickedCratesInfo = {};
        Object.entries(pickedCratesMap).forEach(([groupKey, crateMap]) => {
          const lines = Object.values(crateMap)
            .map((data) => ({
              crateId: data.crateId,
              quantity: data.quantity,
              packageDateUnix: data.packageDateUnix,
              ...(data.subItemProductId ? { subItemProductId: data.subItemProductId } : {}),
            }))
            .filter((line) => line.quantity > 0);
          if (lines.length > 0) pickedCratesInfo[groupKey] = lines;
        });

        if (Object.keys(pickedCratesInfo).length > 0) {
          updateObject.pickedCratesInfo = pickedCratesInfo;
        }
      }

      const orderDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERS_TABLE,
        orderId,
      );

      // Base on the latest Firestore state: `order` is loaded once when the page opens and can be stale
      // (e.g. sooCoolId is written by order processing a few seconds after creation). A stale sooCoolId
      // made the SooCool step create a second delivery instead of updating the existing one.
      const latestOrder = (await getDoc(orderDocRef)).data();
      const updatedOrder = { ...order, ...latestOrder, ...updateObject };

      const packingResult = await handleOrderPackingStatusChange(
        updatedOrder,
        true,
        {
          crates: newCrates,
          updatedBy: email,
          // Pass the pre-picking scannedCrates explicitly, since updatedOrder (above) already
          // has the NEW scannedCrates merged in — using updatedOrder.scannedCrates as "old" here
          // would make old === new and silently skip the stock delta.
          oldScannedCrates: order.scannedCrates || [],
          scannedCrates: scannedCrates || [],
        },
        true, // shouldAutoEmail = true for scanner app
      );

      if (!packingResult.success) {
        message.error(t("error_completing_order"));
        return;
      }

      // Add packing result to update object
      Object.assign(updateObject, packingResult.updateObject);

      // Use the updated order from packing result for all operations
      const finalOrder = { ...packingResult.orderForInvoice, ...updateObject };
      const token = await getFirebaseIDToken();

      // Handle FreshingBox delivery creation if applicable
      if (FRESHING_BOX_DELIVERY_LABELS.includes(finalOrder.deliveryLabel)) {
        sendOrderToFreshingBox(finalOrder, token).catch((error) => {
          console.error("FreshingBox delivery creation failed:", error);
        });
      }

      // Handle SooCool updates if applicable
      console.log("[SooCool debug] shoppingCartChanged/cratesChanged/scannedCratesChanged", {
        shoppingCartChanged,
        cratesChanged,
        scannedCratesChanged,
      });
      if (shoppingCartChanged || cratesChanged || scannedCratesChanged) {
        const sooCoolResult = await handleSooCoolOrderChanges(
          packingResult.orderForInvoice,
          packingResult.orderForInvoice.deliveryLabel,
          {
            deliveryLabelChanged: false,
            deliveryDateChanged: false,
            cratesChanged,
            scannedCratesChanged,
            shoppingCartChanged,
            newOrderData: finalOrder,
          },
          handleSendToSooCool,
          () => Promise.resolve(),
        );

        console.log("[SooCool debug] sooCoolResult", sooCoolResult);

        if (!sooCoolResult.success && sooCoolResult.attempted && !sooCoolResult.error?.includes("Time locked")) {
          console.error("SooCool update failed:", sooCoolResult.error);
        }

        if (sooCoolResult.cutoffTimePassed) {
          console.log("[SooCool debug] showing cutoff notification");
          notification.warning({
            message: t("soo_cool_cutoff_time_passed_title"),
            description: t("soo_cool_cutoff_time_passed"),
            duration: 0,
          });
        }
      }

      // Update stock if cart changed
      if (shoppingCartChanged) {
        try {
          await axios.put(stockApiUrl, finalOrder, {
            headers: { Authorization: token },
          });
        } catch (stockError) {
          console.error("Stock API error:", stockError);
        }
      }

      // Send email notification if cart changed
      if (shoppingCartChanged) {
        try {
          await sendOrderUpdateNotification(
            packingResult.orderForInvoice,
            {
              shoppingCartChanged,
              deliveryDateChanged: false,
              newOrderData: finalOrder,
              updatedOrderProducts: newShoppingCart,
              originalShoppingCart: originalShoppingCart,
            },
            users,
            customers,
            token,
          );
        } catch (emailError) {
          console.error("Email notification error:", emailError);
        }
      }

      // Save order picking as completed
      await saveOrderPickingProgress(ORDER_PICKING_STATUS_COMPLETED);

      // Update Firestore with cart changes, pricing AND packing result fields
      const finalUpdateObject = { ...updateObject, ...packingResult.updateObject };
      await updateDoc(orderDocRef, finalUpdateObject);

      // Send print emails asynchronously (non-blocking)
      if (packingResult.success && packingResult.shouldAutoEmail) {
        axios.post(generateAndEmailDocuments, { orderId }, { headers: { Authorization: token } }).catch((error) => {
          console.error("Failed to send print emails:", error);
          message.error(t("notifications.error_sending_print_emails"));
        });
      }

      message.success(t("order_completed_successfully"));
      navigate(-1);
    } catch (error) {
      console.error("Error finishing order with updates:", error);
      message.error(t("error_completing_order"));
      throw error;
    }
  };

  // Helper function to check if shopping cart quantities actually changed
  const hasShoppingCartActuallyChanged = (originalCart, newCart) => {
    // If different number of products, it changed
    if (originalCart.length !== newCart.length) {
      return true;
    }

    // Check if any product has different quantity
    for (const originalItem of originalCart) {
      const newItem = newCart.find((item) => item.productId === originalItem.productId);

      // Product was removed
      if (!newItem) {
        return true;
      }

      // Quantity changed
      if (originalItem.itemCount !== newItem.itemCount) {
        return true;
      }
    }

    // Check for any new products that weren't in original
    for (const newItem of newCart) {
      const originalItem = originalCart.find((item) => item.productId === newItem.productId);
      if (!originalItem) {
        return true;
      }
    }

    return false;
  };

  const saveIndividualProductProgress = async (productId, fulfilled, ready) => {
    try {
      const docRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERPICKINGS_TABLE,
        orderId,
      );

      const existingDoc = await getDoc(docRef);
      let pickedItems = [];

      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        pickedItems = existingData.pickedItems || [];
      }

      // Get requestedQuantity from current order shopping cart instead of fulfillmentData
      const cartItem = order?.userShoppingCart?.find((item) => item.productId === productId);
      const requestedQuantity = cartItem?.itemCount || 0;

      // Update or add the specific product
      const existingItemIndex = pickedItems.findIndex((item) => item.productId === productId);
      const updatedItem = {
        productId,
        requestedQuantity,
        pickedQuantity: fulfilled,
        isReady: ready,
        bundleData: fulfillmentData[productId]?.bundleData || null, // Save bundleData for unpick/discard
      };

      if (existingItemIndex >= 0) {
        pickedItems[existingItemIndex] = updatedItem;
      } else {
        pickedItems.push(updatedItem);
      }

      await setDoc(
        docRef,
        {
          pickedItems,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Error saving individual product progress:", error);
      throw error;
    }
  };

  const saveOrderPickingProgress = async (status) => {
    try {
      const currentOrder = orders.find((o) => o.id === orderId);

      // Include ALL products from fulfillmentData with their correct picked quantities
      const pickedItems = Object.entries(fulfillmentData).map(([productId, data]) => ({
        productId,
        requestedQuantity: data.requested ?? 0,
        pickedQuantity: data.fulfilled ?? 0,
        isReady: data.ready ?? false,
        bundleData: data.bundleData || null, // Save bundleData for unpick/discard
      }));

      const orderPickingData = {
        farmorderId: currentOrder.farmorderId,
        status: status,
        pickedItems: pickedItems,
        userId: uid,
        userEmail: email,
        updatedAt: dayjs().unix(),
        updatedBy: email,
        orderCommentsProcessed: orderPickingDoc?.orderCommentsProcessed || order.orderCommentsProcessed || false,
        internalNotes: orderPickingDoc?.internalNotes || order.internalNotes || null,
        crates: crateQuantity || 1,
        scannedCrates: scannedCrates || [],
        isPaused: status === ORDER_PICKING_STATUS_IN_PROGRESS,
      };

      // Add completedAt timestamp if status is completed
      if (status === ORDER_PICKING_STATUS_COMPLETED) {
        orderPickingData.completedAt = dayjs().unix();
        orderPickingData.isPaused = true;
      }

      await setDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERPICKINGS_TABLE, orderId),
        orderPickingData,
        { merge: true },
      );

      if (status === ORDER_PICKING_STATUS_IN_PROGRESS) {
        message.success(t("progress_saved"));
      }
    } catch (error) {
      console.error("Error saving order picking progress:", error);
      message.error(t("error_saving_progress"));
      throw error;
    }
  };

  const discardOrderPickingProgress = async () => {
    try {
      const pickingRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERPICKINGS_TABLE,
        orderId,
      );

      // Restock all picked quantities back into the crates they came from before deleting.
      // We use cratePickEvents history so the restock goes to the correct crates.
      const snap = await getDoc(pickingRef);
      if (snap.exists()) {
        const data = snap.data() || {};
        const pickedItems = Array.isArray(data.pickedItems) ? data.pickedItems : [];
        const allEvents = Array.isArray(data.cratePickEvents) ? data.cratePickEvents : [];

        for (const item of pickedItems) {
          const productId = item?.productId;
          const qtyToRestock = Number(item?.pickedQuantity || 0);
          if (!productId || qtyToRestock <= 0) continue;

          const product =
            order?.userShoppingCart?.find((p) => p.productId === productId) ||
            (products || []).find((p) => p?.productId === productId);
          const productBarcode = normalizeBarcode(product?.productBarcode || product?.barcode);

          try {
            // Restock SOLD events directly on this productId (regular products + bundles
            // picked via the regular modal, where no bundleProductId is set on the event).
            await restockCratesForUnpick({
              productId,
              productBarcode,
              quantity: qtyToRestock,
            });

            // Restock sub-item SOLD events for bundles picked via ScanBundleItemsModal.
            // Those events carry bundleProductId = productId and productId = sub-item id.
            // Derive sub-items and quantities directly from the events so this works even
            // for products added during picking (not in originalShoppingCart).
            const subItemIds = [
              ...new Set(
                allEvents
                  .filter((ev) => ev?.type === LOG_ACTION_SOLD && ev?.bundleProductId === productId)
                  .map((ev) => ev.productId),
              ),
            ];

            // Get bundleData from pickedItems to access ALL scanned items (including non-crate items)
            const bundleData = item?.bundleData;
            const allScannedItems = bundleData?.scannedItems || {};

            // Track underlying product quantities for countedStock reversal
            // Use scannedItems which contains ALL quantities (with and without crates)
            const underlyingProductQuantities = omitBundlePackaging(
              allScannedItems,
              productId,
              order?.userShoppingCart,
              products,
            );

            for (const subItemProductId of subItemIds) {
              const itemProduct = (products || []).find((p) => p?.productId === subItemProductId);
              const itemBarcode = normalizeBarcode(itemProduct?.barcode);
              const totalSubQty = allEvents
                .filter(
                  (ev) =>
                    ev?.type === LOG_ACTION_SOLD &&
                    ev?.bundleProductId === productId &&
                    ev?.productId === subItemProductId,
                )
                .reduce((sum, ev) => sum + Number(ev.remainingQuantity ?? ev.quantity ?? 0), 0);

              if (totalSubQty > 0) {
                await restockCratesForUnpick({
                  productId: subItemProductId,
                  productBarcode: itemBarcode,
                  quantity: totalSubQty,
                  bundleProductId: productId,
                });
              }
            }

            // Reverse countedStock adjustments for bundles picked via ScanBundleItemsModal
            if (Object.keys(underlyingProductQuantities).length > 0 && qtyToRestock > 0) {
              try {
                // Decrease countedStock for the bundle (reverse the increase from assembly)
                await updateProductCountedStock(productId, -qtyToRestock);

                // Increase countedStock for each underlying product (reverse the decrease from assembly)
                for (const [underlyingProductId, usedQty] of Object.entries(underlyingProductQuantities)) {
                  if (Number(usedQty) > 0) {
                    await updateProductCountedStock(underlyingProductId, Number(usedQty));
                  }
                }
              } catch (error) {
                console.error("Error reversing countedStock for bundle discard:", error);
                // Don't throw - continue with other products
              }
            }
          } catch (e) {
            console.error("Failed to restock crates for discard", e);
            // Continue attempting to restock other products.
          }
        }
      }

      // Delete the orderPicking document
      await deleteDoc(pickingRef);

      message.success(t("progress_discarded"));
    } catch (error) {
      console.error("Error discarding order picking progress:", error);
      // Don't show error message for non-existent documents
      if (error.code !== "not-found") {
        message.error(t("error_discarding_progress"));
      }
    }
  };

  const getSelectedProductBarcode = () => {
    if (!selectedProduct) return "";

    const fromOrder = normalizeBarcode(selectedProduct.productBarcode);
    if (fromOrder) return fromOrder;

    // Fallback for older orders: use barcode from products collection
    const prod = (products || []).find((p) => p?.productId === selectedProduct.productId);
    return normalizeBarcode(prod?.barcode);
  };

  const findCrateStockByCrateId = (crateId) => {
    const trimmed = String(crateId || "").trim();
    if (!trimmed) return null;
    const docId = sanitizeCrateDocId(trimmed);
    return (
      (crateStocks || []).find(
        (c) => String(c.crateId || "").trim() === trimmed || String(c.id || "").trim() === docId,
      ) || null
    );
  };

  const getCrateShelfLifeStatus = (crate) => {
    const productBarcode = getSelectedProductBarcode();
    const item = getCrateStockItem(crate, productBarcode);
    if (!item) return { overInternal: false, overShelf: false };

    const productId = item?.productId;
    const product = (products || []).find((p) => p?.id === productId || p?.productId === productId) || null;

    const shelfLifeDaysRaw = product?.shelfLife;
    const internalShelfLifeDaysRaw = product?.internalShelfLife;

    const shelfLifeDays = shelfLifeDaysRaw === null || shelfLifeDaysRaw === undefined ? null : Number(shelfLifeDaysRaw);
    const internalShelfLifeDays =
      internalShelfLifeDaysRaw === null || internalShelfLifeDaysRaw === undefined
        ? null
        : Number(internalShelfLifeDaysRaw);

    const packageDateUnix = Number(item?.packageDateUnix || 0);
    if (!packageDateUnix || Number.isNaN(packageDateUnix)) return { overInternal: false, overShelf: false };

    const todayStartUnix = dayjs().startOf("day").unix();
    const ageDays = Math.floor((todayStartUnix - packageDateUnix) / 86400);

    const overShelf = typeof shelfLifeDays === "number" && !Number.isNaN(shelfLifeDays) && ageDays >= shelfLifeDays;
    const overInternal =
      typeof internalShelfLifeDays === "number" &&
      !Number.isNaN(internalShelfLifeDays) &&
      ageDays >= internalShelfLifeDays &&
      !overShelf;

    return {
      overInternal,
      overShelf,
      ageDays,
      shelfLifeDays,
      internalShelfLifeDays,
      packageDate: item?.packageDate || null,
    };
  };

  const isCrateMatchingSelectedProduct = (crate) => {
    const productBarcode = getSelectedProductBarcode();
    if (!productBarcode) return false;

    // Pass productBarcode to find the specific item in fixed crates with multiple products
    const item = getCrateStockItem(crate, productBarcode);
    const crateItemBarcode = normalizeBarcode(item?.barcode);

    return Boolean(crateItemBarcode && crateItemBarcode === productBarcode);
  };

  const handlePickingCrateScan = async (crateBarcode) => {
    const code = String(crateBarcode || "").trim();
    if (!code) return;
    if (!/^[0-9]+$/.test(code) || code.length > CRATE_STOCK_BARCODE_MAX_LENGTH) {
      message.error(t("notifications.error_invalid_barcode"));
      return false;
    }

    if (
      (emptiedCrates || []).some(
        (c) => String(c?.crateId || "").trim() === code || String(c?.crateDocId || "").trim() === code,
      )
    ) {
      message.error(t("order_picking.crate_already_marked_empty"));
      return false;
    }

    const crate = findCrateStockByCrateId(code);
    if (!crate) {
      message.error(t("order_picking.unknown_crate"));
      return false;
    }

    const crateDocId = crate.id || sanitizeCrateDocId(crate.crateId);
    if ((emptiedCrates || []).some((c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim())) {
      message.error(t("order_picking.crate_already_marked_empty"));
      return false;
    }

    // Get the specific item for the current product (important for fixed crates with multiple products)
    const productBarcode = getSelectedProductBarcode();
    const item = getCrateStockItem(crate, productBarcode);
    const itemQty = Number(item?.quantity || 0);
    if (!item || itemQty <= 0) {
      message.error(t("order_picking.crate_is_empty"));
      return false;
    }

    if (!isCrateMatchingSelectedProduct(crate)) {
      message.error(t("order_picking.crate_does_not_contain_product"));
      return false;
    }

    const shelfStatus = getCrateShelfLifeStatus(crate);
    if (shelfStatus.overShelf) {
      // Show confirmation dialog instead of hard fail
      return new Promise((resolve) => {
        confirm({
          title: t("order_picking.crate_product_expired_confirm_title"),
          content: t("order_picking.crate_product_expired_confirm_message"),
          okText: t("order_picking.yes_continue"),
          cancelText: t("order_picking.no_cancel"),
          onOk: () => {
            // User chose to continue with expired product
            if (awaitingCrateScan) {
              setPickingBaseFulfilled(Number(tempFulfilled || 0));
            }
            setPickingCrate(crate);
            setPickingScannedCount(0);
            setAwaitingCrateScan(false);
            resolve(true);
          },
          onCancel: () => {
            // User chose not to use expired product
            resolve(false);
          },
        });
      });
    }
    if (shelfStatus.overInternal) {
      message.warning(t("order_picking.crate_product_over_internal_shelf_life"));
    }

    // When switching crates mid-picking, keep the already fulfilled quantity as the base.
    // This prevents the fulfilled quantity from snapping back when starting with a new crate.
    if (awaitingCrateScan) {
      setPickingBaseFulfilled(Number(tempFulfilled || 0));
    }

    setPickingCrate(crate);
    setPickingScannedCount(0);
    setAwaitingCrateScan(false);

    return true;
  };

  const handlePickingProductScan = async (productBarcode) => {
    const expected = getSelectedProductBarcode();
    const scanned = normalizeBarcode(productBarcode);
    if (!expected) return;
    if (scanned !== expected) {
      message.error(t("notifications.error_unknown_barcode"));
      return;
    }

    if (!pickingCrate) {
      setTempFulfilled((prev) => Number(prev || 0) + 1);
      return;
    }

    const crateQty = Number(getCrateStockItem(pickingCrate, expected)?.quantity || 0);
    if (crateQty <= 0) {
      message.error(t("order_picking.crate_is_empty"));
      return;
    }

    const base = Number(pickingBaseFulfilled || 0);
    const nextScanned = Number(pickingScannedCount || 0) + 1;

    if (nextScanned > crateQty) {
      message.error(t("order_picking.quantity_exceeds_items_in_crate"));
      return;
    }

    const maxAllowed = base + nextScanned;
    setPickingScannedCount(nextScanned);
    setTempFulfilled((prev) => Math.min(maxAllowed, Number(prev || 0) + 1));
  };

  // Wrapper functions that use the utility with local context
  const handleApplySoldToCrate = async ({ crate, soldQty, productIdOrBarcode }) => {
    return await applySoldToCrate({
      crate,
      soldQty,
      productIdOrBarcode,
      orderId,
      farmorderId: order?.farmorderId,
      userEmail: email,
      userId: uid,
    });
  };

  const handleAppendCratePickEvent = async ({
    type = LOG_ACTION_SOLD,
    productId,
    bundleProductId,
    crateDocId,
    crateId,
    quantity,
    productBarcode,
    remainingQuantity,
    packageDateUnix,
  }) => {
    const next = await appendOrderPickingEvent({
      orderId,
      type,
      productId,
      bundleProductId,
      crateDocId,
      crateId,
      quantity,
      productBarcode,
      remainingQuantity,
      packageDateUnix,
      userEmail: email,
    });

    setOrderPickingDoc((prev) => ({
      ...(prev || {}),
      cratePickEvents: next,
    }));
  };

  const restockCratesForUnpick = async ({ productId, productBarcode, quantity, bundleProductId = null }) => {
    const qtyToRestock = Number(quantity || 0);
    if (qtyToRestock <= 0) return;

    const pickingRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_ORDERPICKINGS_TABLE,
      orderId,
    );

    const snap = await getDoc(pickingRef);
    const data = snap.data() || {};
    const events = Array.isArray(data.cratePickEvents) ? data.cratePickEvents : [];

    let remaining = qtyToRestock;
    const nextEvents = [...events];

    const plannedActions = [];

    // Plan which crates to restock from (LIFO - last in, first out)
    for (let i = nextEvents.length - 1; i >= 0 && remaining > 0; i -= 1) {
      const ev = nextEvents[i];
      if (ev?.type !== LOG_ACTION_SOLD) continue;

      // For bundle sub-items, match by bundleProductId; for regular products, match by productId
      const isMatch = bundleProductId
        ? ev?.bundleProductId === bundleProductId && ev?.productId === productId
        : ev?.productId === productId && !ev?.bundleProductId;

      if (!isMatch) continue;

      const evRemaining = Number(ev.remainingQuantity ?? ev.quantity ?? 0);
      if (evRemaining <= 0) continue;

      const useQty = Math.min(remaining, evRemaining);
      remaining -= useQty;

      const crateDocId = ev.crateDocId;
      if (!crateDocId) continue;

      plannedActions.push({ index: i, crateDocId, evRemaining, useQty, crateId: ev.crateId || crateDocId });
    }

    // Preflight validation + restore wipe (only if crate is still available and empty)
    const uniqueCrateDocIds = [...new Set(plannedActions.map((a) => a.crateDocId))];
    const restoredEventsMap = new Map();

    for (const crateDocId of uniqueCrateDocIds) {
      const crateRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_CRATE_STOCKS_TABLE,
        crateDocId,
      );
      const crateSnap = await getDoc(crateRef);
      const crateData = crateSnap.data() || {};
      const crateItems = Array.isArray(crateData.items)
        ? crateData.items
        : Array.isArray(crateData.products)
          ? crateData.products
          : [];

      const currentItem = crateItems[0] || null;
      const currentQty = Number(currentItem?.quantity || 0);

      // If the crate is currently empty, it may have been wiped (events flushed).
      // Only allow restoring + restocking if the crate is still available.
      if (currentQty <= 0) {
        const status = crateData?.status || "available";
        const isEmpty = crateItems.length === 0;

        if (status !== "available" || !isEmpty) {
          const err = new Error("UNPICK_CRATE_NOT_AVAILABLE");
          err.code = "UNPICK_CRATE_NOT_AVAILABLE";
          err.crateDocId = crateDocId;
          throw err;
        }

        // Use utility function to restore events from log
        const restoreResult = await restoreCrateEventsFromLog({ crateDocId });
        if (restoreResult && restoreResult.events) {
          restoredEventsMap.set(crateDocId, restoreResult.events);
        }
      }
    }

    // Apply restock actions using utility functions
    const product = products?.find((p) => p.productId === productId || p.id === productId);
    const productName = product?.productName || product?.name?.en || null;

    for (const action of plannedActions) {
      const { index: i, crateDocId, evRemaining, useQty, crateId } = action;

      const ev = nextEvents[i];
      if (!ev) continue;

      // Get item metadata from restored events if crate was empty
      const restoredEvents = restoredEventsMap.get(crateDocId);
      let itemMetadata = {};

      if (restoredEvents) {
        itemMetadata = extractItemMetadataFromEvents(restoredEvents, productId);
      } else {
        // Fallback: try to read from events subcollection
        const crateEvents = await getCrateEvents({ crateDocId });
        itemMetadata = extractItemMetadataFromEvents(crateEvents, productId);
      }

      // Use utility function to restock the crate
      await restockCrate({
        crateDocId,
        crateId,
        productId,
        productName,
        productBarcode,
        quantity: useQty,
        orderId,
        farmorderId: order?.farmorderId,
        company: null, // Will be retrieved from crate data in utility
        userEmail: email,
        userId: uid,
        itemMetadata,
      });

      // Update event remaining quantity
      nextEvents[i] = {
        ...ev,
        remainingQuantity: evRemaining - useQty,
      };
    }

    // Add RESTOCKED_UNPICK events to the orderPicking log for each restock action
    for (const action of plannedActions) {
      const ev = nextEvents[action.index];
      if (!ev) continue;

      nextEvents.push({
        type: LOG_ACTION_RESTOCKED_UNPICK,
        productId,
        productBarcode: productBarcode || null,
        bundleProductId: bundleProductId || null,
        crateDocId: action.crateDocId,
        crateId: action.crateId,
        quantity: action.useQty,
        remainingQuantity: action.useQty,
        packageDateUnix: ev.packageDateUnix || null,
        createdAtUnix: dayjs().unix(),
        createdBy: uid || email || null,
      });
    }

    // Update orderPicking document with modified events
    await setDoc(
      pickingRef,
      { cratePickEvents: nextEvents, updatedAt: dayjs().unix(), updatedBy: uid || email || null },
      { merge: true },
    );
  };

  const handleBarcodeScan = async (barcode) => {
    if (productModalVisible && selectedProduct) {
      const code = String(barcode || "").trim();

      // In the product modal we normally only listen for product barcodes.
      // Only when the user explicitly indicates the crate is empty (or no crate was scanned yet), we listen for a new crate.
      if (awaitingCrateScan || !pickingCrate) {
        if (/^[0-9]+$/.test(code) && code.length <= CRATE_STOCK_BARCODE_MAX_LENGTH) {
          await handlePickingCrateScan(code);
          return;
        }

        await handlePickingProductScan(code);
        return;
      }

      await handlePickingProductScan(code);
      return;
    }

    // Check crate barcodes first (fast check, avoids product lookups)
    if (CRATE_BARCODES.includes(barcode)) {
      const crate = CRATES.find((c) => c.barcode === barcode);

      setScannedCrates((prev) => {
        const existing = prev.find((c) => c.barcode === barcode);
        if (existing) {
          return prev.map((c) => (c.barcode === barcode ? { ...c, quantity: c.quantity + 1 } : c));
        } else {
          return [...prev, { barcode, quantity: 1, size: crate.size }];
        }
      });

      return;
    }

    // Allow scanning a crate stock barcode directly to open the product modal
    // Crate stock barcodes are 13-digit numeric codes.
    if (
      /^[0-9]+$/.test(String(barcode || "").trim()) &&
      String(barcode || "").trim().length <= CRATE_STOCK_BARCODE_MAX_LENGTH
    ) {
      const crate = findCrateStockByCrateId(barcode);
      if (crate) {
        const item = getCrateStockItem(crate);
        const itemQty = Number(item?.quantity || 0);
        if (!item || itemQty <= 0) {
          message.error(t("order_picking.crate_is_empty"));
          return;
        }

        const shelfStatus = getCrateShelfLifeStatus(crate);
        if (shelfStatus.overShelf) {
          message.error(t("order_picking.crate_product_over_shelf_life"));
          return;
        }
        if (shelfStatus.overInternal) {
          message.warning(t("order_picking.crate_product_over_internal_shelf_life"));
        }

        const crateProductId = item?.productId || null;
        const crateProductBarcode = normalizeBarcode(item?.barcode);

        const fromOrder = (order?.userShoppingCart || []).find((p) => p?.productId === crateProductId);
        const fromOrderByBarcode =
          !fromOrder && crateProductBarcode
            ? (order?.userShoppingCart || []).find((p) => normalizeBarcode(p?.productBarcode) === crateProductBarcode)
            : null;

        const orderItem = fromOrder || fromOrderByBarcode || null;
        if (!orderItem?.productId) {
          message.error(t("order_picking.crate_product_not_in_order"));
          return;
        }

        openProductModal(orderItem.productId, crate);
        return;
      }
    }

    // Try to find product in userShoppingCart first
    let productData = order.userShoppingCart?.find((p) => p.productBarcode === barcode);
    // Otherwise, try to find product in products collection (for new products and older orders without barcodes)
    if (!productData) {
      productData = getProductByBarcode(barcode);
    }
    if (productData) {
      const currentFulfillment = fulfillmentData[productData.productId] || {
        requested: 0,
        fulfilled: 0,
        ready: false,
      };

      // Logic to handle an existing product that is not yet in the order
      if (!currentFulfillment.requested && !currentFulfillment.fulfilled) {
        const availableProduct = availableProducts.find((p) => p.productId === productData.productId);
        if (availableProduct) {
          handleAddProduct(availableProduct);
        } else return;
      }

      const nextFulfilled = currentFulfillment.fulfilled + 1;

      await handleUpdateFulfillment(productData?.productId, nextFulfilled, true);
    } else {
      message.error(t("notifications.error_unknown_barcode"));
    }
  };

  const openProductModal = (productId, initialCrate = null) => {
    const productData = order.userShoppingCart?.find((p) => p.productId === productId);
    if (!productData) return;

    const currentFulfillment = fulfillmentData[productId] || {
      requested: productData.itemCount,
      fulfilled: 0,
      ready: false,
    };

    // Get bundle info from products collection if not in cart
    const productFromCollection = products?.find((p) => p.productId === productId);
    const bundleInfo = productFromCollection?.isBundle
      ? {
          isBundle: true,
          bundleItems: productData.bundleItems || productFromCollection.bundleItems,
          packagingMaterial: productData.packagingMaterial || productFromCollection.packagingMaterial,
          hasCustomPrice: productData.hasCustomPrice || productFromCollection.hasCustomPrice,
        }
      : {};

    setSelectedProduct({
      ...productData,
      ...bundleInfo,
      ...currentFulfillment,
    });

    // Initialize temp fulfilled with current value
    setTempFulfilled(currentFulfillment.fulfilled);
    setPickingBaseFulfilled(currentFulfillment.fulfilled);
    setPickingCrate(initialCrate);
    setPickingScannedCount(0);
    setAwaitingCrateScan(!initialCrate);
    setEmptiedCrates([]);
    setPickedCrates([]);

    // Restore bundle data if it exists (persists across modal open/close)
    const bundleData = currentFulfillment.bundleData || null;
    setScannedBundleItems(bundleData);

    setProductModalVisible(true);
  };

  const handleProductClick = (productId) => {
    openProductModal(productId, null);
  };

  const handleUpdateFulfillment = async (productId, fulfilled, ready = true) => {
    // Update local state immediately
    setFulfillmentData((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        fulfilled,
        ready: true, // Always set to ready when updating
      },
    }));

    // Save to Firebase immediately to prevent state loss
    try {
      setIsSaving(true);
      await saveIndividualProductProgress(productId, fulfilled, true);
    } catch (error) {
      console.error("Error saving individual product progress:", error);
      // Revert local state on error
      setFulfillmentData((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          fulfilled: prev[productId]?.fulfilled || 0,
          ready: prev[productId]?.ready || false,
        },
      }));
      message.error(t("error_saving_progress"));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset picked state for a product so picking can start over
  const handleUnpick = async (productId) => {
    // Debouncing: prevent multiple simultaneous unpick operations for the same product
    if (unpickingInProgressRef.current.has(productId)) {
      console.warn(`Unpick already in progress for product ${productId}, ignoring duplicate call`);
      return;
    }

    // Mark this product as being unpicked
    unpickingInProgressRef.current.add(productId);

    try {
      const qtyToRestock = Number(fulfillmentData?.[productId]?.fulfilled || 0);
      const productBarcode = normalizeBarcode(
        order?.userShoppingCart?.find((p) => p.productId === productId)?.productBarcode ||
          (products || []).find((p) => p?.productId === productId)?.barcode,
      );

      // Update local state immediately
      setFulfillmentData((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          fulfilled: 0,
          ready: false,
          bundleData: null, // Clear bundle data when unpicking
        },
      }));

      // Persist to Firestore
      setIsSaving(true);

      if (qtyToRestock > 0) {
        // Restock SOLD events directly on this productId (regular products + bundles
        // picked via the regular modal, where no bundleProductId is set on the event).
        await restockCratesForUnpick({
          productId,
          productBarcode,
          quantity: qtyToRestock,
        });

        // Restock sub-item SOLD events for bundles picked via ScanBundleItemsModal.
        // Those events carry bundleProductId = productId and productId = sub-item id.
        // Derive sub-items and quantities directly from events so this works even
        // for products added during picking (not in originalShoppingCart).
        const allEvents = Array.isArray(orderPickingDoc?.cratePickEvents) ? orderPickingDoc.cratePickEvents : [];
        const subItemIds = [
          ...new Set(
            allEvents
              .filter((ev) => ev?.type === LOG_ACTION_SOLD && ev?.bundleProductId === productId)
              .map((ev) => ev.productId),
          ),
        ];

        // Get bundleData to access ALL scanned items (including non-crate items)
        const bundleData = fulfillmentData?.[productId]?.bundleData;
        const allScannedItems = bundleData?.scannedItems || {};

        // Track underlying product quantities for countedStock reversal
        // Use scannedItems which contains ALL quantities (with and without crates)
        const underlyingProductQuantities = omitBundlePackaging(
          allScannedItems,
          productId,
          order?.userShoppingCart,
          products,
        );

        for (const subItemProductId of subItemIds) {
          const itemProduct = (products || []).find((p) => p?.productId === subItemProductId);
          const itemBarcode = normalizeBarcode(itemProduct?.barcode);
          const totalSubQty = allEvents
            .filter(
              (ev) =>
                ev?.type === LOG_ACTION_SOLD && ev?.bundleProductId === productId && ev?.productId === subItemProductId,
            )
            .reduce((sum, ev) => sum + Number(ev.remainingQuantity ?? ev.quantity ?? 0), 0);

          if (totalSubQty > 0) {
            await restockCratesForUnpick({
              productId: subItemProductId,
              productBarcode: itemBarcode,
              quantity: totalSubQty,
              bundleProductId: productId,
            });
          }
        }

        // Reverse countedStock adjustments for bundles picked via ScanBundleItemsModal
        if (Object.keys(underlyingProductQuantities).length > 0 && qtyToRestock > 0) {
          try {
            // Decrease countedStock for the bundle (reverse the increase from assembly)
            await updateProductCountedStock(productId, -qtyToRestock);

            // Increase countedStock for each underlying product (reverse the decrease from assembly)
            for (const [underlyingProductId, usedQty] of Object.entries(underlyingProductQuantities)) {
              if (Number(usedQty) > 0) {
                await updateProductCountedStock(underlyingProductId, Number(usedQty));
              }
            }
          } catch (error) {
            console.error("Error reversing countedStock for bundle unpick:", error);
            message.warning(t("order_picking.counted_stock_update_failed"));
          }
        }
      }

      await saveIndividualProductProgress(productId, 0, false);
      setProductModalVisible(false);
      setPickingCrate(null);
      setPickingScannedCount(0);
      setScannedBundleItems(null);
    } catch (error) {
      console.error("Error unpicking product:", error);
      // Revert local state on error
      setFulfillmentData((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
        },
      }));

      if (error?.code === "UNPICK_CRATE_NOT_AVAILABLE") {
        message.error(t("order_picking.unpick_crate_not_available"));
      } else {
        message.error(t("error_saving_progress"));
      }
    } finally {
      setIsSaving(false);
      // Remove productId from in-progress tracking
      unpickingInProgressRef.current.delete(productId);
    }
  };

  const handleAddProduct = (product) => {
    const finalPrice = applyCustomerPricingToProduct(product, customer, priceLists, getCountriesMap());

    // Calculate bundle item prices with invoice surcharge and customer discount
    let bundleItemsWithPricing = [];
    let packagingMaterialWithPricing = null;
    let recalculatedBundlePrice = finalPrice;

    if (product.isBundle && product.bundleItems) {
      const allItemsWithPricing = getBundleItemsWithCustomerPricing(
        product,
        customer,
        finalPrice,
        products,
        priceLists,
        getCountriesMap(),
      );

      // Separate packaging items from regular bundle items
      bundleItemsWithPricing = allItemsWithPricing.filter((item) => !item.isPackaging);
      const packagingItem = allItemsWithPricing.find((item) => item.isPackaging);

      // Packaging keeps original price (no invoice surcharge or discount)
      if (packagingItem) {
        packagingMaterialWithPricing = packagingItem;
      } else if (product.packagingMaterial) {
        packagingMaterialWithPricing = product.packagingMaterial;
      }

      // Recalculate bundle price: sum of bundle items + packaging (packaging excluded from discount)
      const bundleItemsTotal = bundleItemsWithPricing.reduce((sum, item) => {
        return sum + parseFloat(item.price) * item.quantity;
      }, 0);
      const packagingPrice = packagingMaterialWithPricing ? parseFloat(packagingMaterialWithPricing.price || 0) : 0;
      recalculatedBundlePrice = (bundleItemsTotal + packagingPrice).toFixed(2);
    }

    const newProductData = {
      productId: product.productId,
      productName: product.productName,
      itemCount: 0,
      productPrice: recalculatedBundlePrice,
      productBarcode: product.barcode || "",
      // Store bundle pricing data if this is a bundle
      ...(product.isBundle && {
        isBundle: true,
        bundleItems: bundleItemsWithPricing,
        packagingMaterial: packagingMaterialWithPricing,
        hasCustomPrice: product.hasCustomPrice || false,
      }),
    };

    // Add to order's shopping cart (locally)
    setOrder((prev) => ({
      ...prev,
      userShoppingCart: [...(prev.userShoppingCart || []), newProductData],
    }));

    // Add to fulfillment data
    setFulfillmentData((prev) => ({
      ...prev,
      [product.productId]: {
        requested: 0,
        fulfilled: 0,
        ready: false,
      },
    }));

    setAddProductModalVisible(false);
    setSearchProduct("");
  };

  const handleRemoveProduct = (productId) => {
    // Remove from order's shopping cart (locally)
    setOrder((prev) => ({
      ...prev,
      userShoppingCart: prev.userShoppingCart?.filter((p) => p.productId !== productId) || [],
    }));

    // Remove from fulfillment data
    setFulfillmentData((prev) => {
      const newData = { ...prev };
      delete newData[productId];
      return newData;
    });

    setProductModalVisible(false);
    setPickingCrate(null);
    setPickingScannedCount(0);
  };

  const handleBundleScanSave = async ({
    completedBundles,
    scannedItems,
    pickedCrates,
    emptiedCrates,
    currentPickingCrate,
    currentPickingScannedCount,
    currentActiveProductId,
  }) => {
    if (!selectedProduct) return;

    const productId = selectedProduct.productId;

    // Store scanned bundle items for display in product detail modal
    // This data will be used when the user clicks Save in the product detail modal
    const bundleData = {
      completedBundles,
      scannedItems,
      bundleItems: selectedProduct?.bundleItems || [],
      packagingMaterial: selectedProduct?.packagingMaterial || null,
      pickedCrates,
      emptiedCrates,
      currentPickingCrate,
      currentPickingScannedCount,
      currentActiveProductId,
    };

    setScannedBundleItems(bundleData);

    // Update temp fulfilled by adding the newly completed bundles to the existing value
    setTempFulfilled((prev) => Number(prev || 0) + Number(completedBundles || 0));

    // Close only the scan bundle modal; keep the product detail modal open
    setScanBundleModalVisible(false);
    setPickingCrate(null);
    setPickingScannedCount(0);
  };

  const handleProcessComments = async () => {
    try {
      // Save to orderPicking document
      await setDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERPICKINGS_TABLE, orderId),
        { orderCommentsProcessed: true, updatedAt: dayjs().unix(), updatedBy: email },
        { merge: true },
      );

      // Update local orderPickingDoc state
      setOrderPickingDoc((prev) => ({
        ...prev,
        orderCommentsProcessed: true,
      }));

      // Close the popover
      setRemarksPopoverVisible(false);

      message.success(t("notifications.order_comments_processed"));
    } catch (err) {
      console.log(err);
      message.error(t("notifications.error_processing_order_comments"));
    }
  };

  const handleSaveInternalNotes = () => {
    internalNotesForm.validateFields().then(async (values) => {
      setNotesLoading(true);
      if (order.internalNotes || orderPickingDoc?.internalNotes) {
        if (
          order.internalNotes.text !== values.internalNotes ||
          orderPickingDoc?.internalNotes.text !== values.internalNotes
        ) {
          values.internalNotes = {
            ...order.internalNotes,
            text: values.internalNotes || "",
            updatedAt: dayjs().unix(),
            updatedBy: email,
          };
        } else {
          values.internalNotes = order.internalNotes;
        }
      } else {
        values.internalNotes = {
          text: values.internalNotes || "",
          createdAt: dayjs().unix(),
          createdBy: email,
        };
      }

      try {
        await setDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERPICKINGS_TABLE, orderId),
          { internalNotes: values.internalNotes, updatedAt: dayjs().unix(), updatedBy: email },
          { merge: true },
        );
        setOrderPickingDoc((prev) => ({
          ...prev,
          internalNotes: values.internalNotes,
        }));

        setOrder((prev) => ({
          ...prev,
          internalNotes: values.internalNotes,
        }));

        setNotesModalVisible(false);
        message.success(t("notifications.internal_notes_processed"));
      } catch (error) {
        console.log(error);
        message.error(t("notifications.error_processing_internal_notes"));
      } finally {
        setNotesLoading(false);
      }
    });
  };

  // Filter products for the company (including bundles)
  const availableProducts =
    products?.filter((product) => {
      // Include bundles even if they don't have productInSeason, or products that are in season
      if (!product.isBundle && !product.productInSeason) return false;
      if (companyFilter !== "All" && product.company !== companyFilter) return false;
      // Don't show products already in the order
      return !order?.userShoppingCart?.some((item) => item.productId === product.productId);
    }) || [];

  if (isLoading || ordersLoading || customersLoading || leadsLoading || productsLoading || crateStocksLoading) {
    return <Loading />;
  }

  if (!order) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Text>Order not found</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "16px",
        backgroundColor: order?.deliveryLabel === ADDITIONAL_DELIVERY_LABEL ? "#e6f4ff" : "transparent",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        {/*<img src={logoSrc} alt={logoAlt} style={{ height: "40px", marginBottom: "16px" }} />*/}
        <Title level={3} style={{ margin: 0, paddingTop: 10 }}>
          {(order?.type === "acquisition" ? leads.find((l) => l.leadId === order.leadId)?.companyName : null) ||
            customer?.customerName ||
            order?.farmorderCustomerName ||
            "Unknown Customer"}
        </Title>

        {/* Delivery Date */}
        <div style={{ marginBottom: "8px" }}>
          <Text style={{ fontSize: "16px" }}>
            {t("delivery_date")}: {deliveryDate}
          </Text>
        </div>

        {order.packed && (
          <div style={{ marginTop: "8px" }}>
            <Badge status="success" text={t("order_already_fulfilled")} />
          </div>
        )}
      </div>

      {/* Action buttons */}
      <Row gutter={8} style={{ marginBottom: "24px" }}>
        <Col span={12}>
          <Button block onClick={handleExitWithoutFinishing} icon={<ArrowLeftOutlined />}>
            {t("exit")}
          </Button>
        </Col>
        <Col span={12}>
          <Button
            block
            type="primary"
            onClick={handleOrderPickingFinish}
            icon={<CheckOutlined />}
            disabled={order.packed}
            loading={finishLoading}
          >
            {t("finish")}
          </Button>
        </Col>
      </Row>

      {/* ADD Delivery Alert */}
      {order.deliveryLabel === ADDITIONAL_DELIVERY_LABEL && (
        <Alert
          message={`${t("add_delivery")}${mainOrder.farmorderId}`}
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Order info card */}
      <Card size="small" style={{ marginBottom: "16px" }}>
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div>
            <Text strong>#{order.farmorderId}</Text>
            <br />
            <Text type="secondary">{order.deliveryLabel || "No Label"}</Text>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, flex: 0 }}>
            {order.farmorderComments ? (
              <Popover
                content={
                  <>
                    <p style={{ marginBottom: 16, maxWidth: "250px" }}>{order.farmorderComments}</p>
                    {!order.orderCommentsProcessed && !orderPickingDoc?.orderCommentsProcessed && (
                      <Button size="small" type="primary" onClick={handleProcessComments}>
                        {t("mark_as_processed")}
                      </Button>
                    )}
                  </>
                }
                title={t("order_remarks")}
                trigger="click"
                open={remarksPopoverVisible}
                onOpenChange={setRemarksPopoverVisible}
              >
                <Button type={"default"} size={"small"}>
                  <Badge
                    status={
                      order.orderCommentsProcessed || orderPickingDoc?.orderCommentsProcessed ? "success" : "warning"
                    }
                  />
                  {t("has_remarks")}
                </Button>
              </Popover>
            ) : (
              <Button type={"default"} disabled size={"small"}>
                <Badge status="success" />
                <Text type="secondary">{t("no_remarks")}</Text>
              </Button>
            )}
            {hasInternalNotes ? (
              <Popover
                content={
                  <>
                    <p style={{ marginBottom: 16, maxWidth: "250px" }}>
                      {orderPickingDoc?.internalNotes?.text || order.internalNotes.text}
                    </p>
                    <Text type={"secondary"}>
                      {orderPickingDoc?.internalNotes?.updatedBy ||
                        orderPickingDoc?.internalNotes?.createdBy ||
                        order.internalNotes.updatedBy ||
                        order.internalNotes.createdBy}
                    </Text>
                    <br />
                    <Text type="secondary">
                      {dayjs
                        .unix(
                          orderPickingDoc?.internalNotes?.updatedAt ||
                            orderPickingDoc?.internalNotes?.createdAt ||
                            order.internalNotes.updatedAt ||
                            order.internalNotes.createdAt,
                        )
                        .format("DD-MM-YYYY, HH:mm")}
                    </Text>{" "}
                    <Button
                      type={"default"}
                      icon={<EditOutlined />}
                      size={"small"}
                      block
                      disabled={order.packed}
                      onClick={() => {
                        internalNotesForm.setFieldsValue({
                          internalNotes: orderPickingDoc?.internalNotes?.text || order.internalNotes.text,
                        });
                        setNotesPopoverVisible(false);
                        setNotesModalVisible(true);
                      }}
                    >
                      {t("edit")}
                    </Button>
                  </>
                }
                title={t("internal_notes")}
                trigger="click"
                open={notesPopoverVisible}
                onOpenChange={setNotesPopoverVisible}
              >
                <Badge dot>
                  <Button
                    type={"default"}
                    size={"small"}
                    danger
                    block
                    icon={<InfoCircleOutlined />}
                    style={{ boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.2)" }}
                  >
                    {t("read_notes")}
                  </Button>
                </Badge>
              </Popover>
            ) : (
              <Button
                type={"default"}
                icon={<PlusOutlined />}
                size={"small"}
                block
                disabled={order.packed}
                onClick={() => setNotesModalVisible(true)}
              >
                {t("add_notes")}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Scanned Crates UI */}
      <Card
        title={t("packaging")}
        size="small"
        style={{ marginTop: "16px" }}
        extra={
          <Button
            type="default"
            icon={scannedCrates.length > 0 ? <EditOutlined /> : <PlusOutlined />}
            size="small"
            disabled={order.packed}
            onClick={() => {
              setTempScannedCrates([...scannedCrates]);
              setCratesModalVisible(true);
            }}
          >
            {scannedCrates.length > 0 ? t("edit") : t("add_packaging")}
          </Button>
        }
      >
        {scannedCrates.length > 0 ? (
          <Text>
            {scannedCrates
              .map((crate) => {
                const sizeLabel = crate.size ? crate.size.toUpperCase() : "?";
                return `${sizeLabel} x${crate.quantity}`;
              })
              .join(", ")}
          </Text>
        ) : (
          <Text type="secondary">{t("no_crates_added")}</Text>
        )}
      </Card>

      {/* Shipping Cost Toggle Card */}
      {/*<Card title={t("shipping_costs")} size="small" style={{ marginTop: "16px" }}>*/}
      {/*  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>*/}
      {/*    <Text>{t("enable_shipping_cost")}</Text>*/}
      {/*    <Switch*/}
      {/*      checked={addShippingCost}*/}
      {/*      onChange={setAddShippingCost}*/}
      {/*      checkedChildren={t("yes")}*/}
      {/*      unCheckedChildren={t("no")}*/}
      {/*      disabled={order.packed}*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*</Card>*/}

      {/* Products list */}
      <Card title={t("products_to_fulfill")} size="small" style={{ marginTop: "16px" }}>
        <List
          dataSource={(order.userShoppingCart || []).filter((item) => {
            // Altijd tonen als:
            // 1. Requested quantity > 0, OF
            // 2. Product is nieuw toegevoegd (staat niet in original order)
            const wasInOriginalOrder = originalShoppingCart.some((orig) => orig.productId === item.productId);
            return item.itemCount > 0 || !wasInOriginalOrder;
          })}
          renderItem={(item) => {
            const fulfillment = fulfillmentData[item.productId] || {
              requested: item.itemCount,
              fulfilled: 0,
              ready: false,
            };

            // Determine background color: different color when ready but partially fulfilled
            const isPartialReady = fulfillment.ready && fulfillment.fulfilled < fulfillment.requested;
            const itemBg = fulfillment.ready ? (isPartialReady ? "#fffbe6" : "#dcfce7") : "white";

            return (
              <List.Item
                onClick={() => !order.packed && handleProductClick(item.productId)}
                style={{
                  cursor: order.packed ? "default" : "pointer",
                  backgroundColor: itemBg,
                  border: "1px solid #f0f0f0",
                  borderRadius: "8px",
                  marginBottom: "8px",
                  padding: "12px",
                  opacity: order.packed ? 0.7 : 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <div style={{ marginRight: "12px" }}>
                    <Text strong>
                      {fulfillment.fulfilled}/{fulfillment.requested}
                    </Text>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text>{item.productName}</Text>
                  </div>
                  <div>
                    <Badge
                      status={fulfillment.ready ? "success" : "warning"}
                      text={fulfillment.ready ? t("ready") : t("pending")}
                    />
                  </div>
                </div>
              </List.Item>
            );
          }}
          locale={{
            emptyText: t("no_products_in_order"),
          }}
        />
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={() => setAddProductModalVisible(true)}
            disabled={order.packed}
          >
            {t("add_product")}
          </Button>
        </div>
      </Card>

      {/* Product detail modal */}
      <ProductDetailModal
        selectedProduct={selectedProduct}
        visible={productModalVisible}
        scannedBundleItems={scannedBundleItems}
        setScannedBundleItems={setScannedBundleItems}
        fulfillmentData={fulfillmentData}
        setFulfillmentData={setFulfillmentData}
        order={order}
        originalShoppingCart={originalShoppingCart}
        pickingCrate={pickingCrate}
        setPickingCrate={setPickingCrate}
        awaitingCrateScan={awaitingCrateScan}
        setAwaitingCrateScan={setAwaitingCrateScan}
        pickingScannedCount={pickingScannedCount}
        setPickingScannedCount={setPickingScannedCount}
        pickingBaseFulfilled={pickingBaseFulfilled}
        setPickingBaseFulfilled={setPickingBaseFulfilled}
        emptiedCrates={emptiedCrates}
        setEmptiedCrates={setEmptiedCrates}
        pickedCrates={pickedCrates}
        setPickedCrates={setPickedCrates}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        tempFulfilled={tempFulfilled}
        setTempFulfilled={setTempFulfilled}
        setProductModalVisible={setProductModalVisible}
        setScanBundleModalVisible={setScanBundleModalVisible}
        handleRemoveProduct={handleRemoveProduct}
        handleUnpick={handleUnpick}
        getCrateStockItem={getCrateStockItem}
        applySoldToCrate={handleApplySoldToCrate}
        appendCratePickEvent={handleAppendCratePickEvent}
        handleUpdateFulfillment={handleUpdateFulfillment}
        restockCratesForUnpick={restockCratesForUnpick}
        handlePickingCrateScan={handlePickingCrateScan}
        getSelectedProductBarcode={getSelectedProductBarcode}
        getPersistedCrateLinesForProduct={getPersistedCrateLinesForProduct}
        findCrateStockByCrateId={findCrateStockByCrateId}
        crateStocks={crateStocks}
        products={products}
      />

      {/* Add product modal */}
      <Modal
        title={t("add_product")}
        open={addProductModalVisible}
        onCancel={() => {
          setAddProductModalVisible(false);
          setSearchProduct("");
        }}
        footer={null}
      >
        <Select
          showSearch
          placeholder={t("search_products")}
          value={searchProduct}
          onChange={setSearchProduct}
          onSelect={(value) => {
            const product = availableProducts.find((p) => p.productId === value);
            if (product) {
              handleAddProduct(product);
            }
          }}
          style={{ width: "100%", marginBottom: "16px" }}
          filterOption={(input, option) => {
            const product = availableProducts.find((p) => p.productId === option.value);
            return product?.productName?.toLowerCase().includes(input.toLowerCase()) || false;
          }}
          size="large"
        >
          {availableProducts.map((product) => (
            <Option key={product.productId} value={product.productId}>
              {product.productName}
            </Option>
          ))}
        </Select>
      </Modal>

      {/* Internal notes modal */}
      <Modal
        title={t("internal_notes")}
        open={notesModalVisible}
        onCancel={() => setNotesModalVisible(false)}
        okText={t("save")}
        cancelText={t("cancel")}
        onOk={handleSaveInternalNotes}
        okButtonProps={{ size: "small", loading: notesLoading }}
        cancelButtonProps={{ size: "small" }}
      >
        <Form form={internalNotesForm} layout={"vertical"}>
          <Row>
            <Col xs={24}>
              <Form.Item name={"internalNotes"}>
                <Input.TextArea placeholder={t("internal_notes_scanner_placeholder")} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Crates Management Modal */}
      <Modal
        title={t("add_packaging")}
        open={cratesModalVisible}
        onCancel={() => {
          setTempScannedCrates([]);
          setCratesModalVisible(false);
        }}
        onOk={() => {
          setScannedCrates([...tempScannedCrates]);
          const totalQuantity = tempScannedCrates.reduce((sum, crate) => sum + crate.quantity, 0);
          setCrateQuantity(totalQuantity || 1);
          setCratesModalVisible(false);
          setTempScannedCrates([]);
        }}
        okText={t("save")}
        cancelText={t("cancel")}
        okButtonProps={{ size: "large" }}
        cancelButtonProps={{ size: "large" }}
        width={400}
      >
        <div style={{ marginTop: 16 }}>
          {CRATES.map((crate) => {
            const existingCrate = tempScannedCrates.find((c) => c.barcode === crate.barcode);
            const currentQuantity = existingCrate?.quantity || 0;

            return (
              <div
                key={crate.barcode}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "30px 10px 30px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Text style={{ fontSize: 24 }}>
                  {crate.size === "s" ? "Small" : crate.size === "m" ? "Medium" : "Large"}
                </Text>
                <QuantityControl
                  isMobile={true}
                  productId={crate.barcode}
                  currentQuantity={currentQuantity}
                  onIncrease={() => {
                    setTempScannedCrates((prev) => {
                      const existing = prev.find((c) => c.barcode === crate.barcode);
                      if (existing) {
                        return prev.map((c) => (c.barcode === crate.barcode ? { ...c, quantity: c.quantity + 1 } : c));
                      } else {
                        return [...prev, { barcode: crate.barcode, quantity: 1, size: crate.size }];
                      }
                    });
                  }}
                  onDecrease={() => {
                    if (currentQuantity > 0) {
                      setTempScannedCrates((prev) => {
                        const updated = prev.map((c) =>
                          c.barcode === crate.barcode ? { ...c, quantity: Math.max(0, c.quantity - 1) } : c,
                        );
                        return updated.filter((c) => c.quantity > 0);
                      });
                    }
                  }}
                  onManualInput={(id, value) => {
                    setTempScannedCrates((prev) => {
                      const existing = prev.find((c) => c.barcode === crate.barcode);
                      if (value > 0) {
                        if (existing) {
                          return prev.map((c) =>
                            c.barcode === crate.barcode ? { ...c, quantity: Math.max(0, value) } : c,
                          );
                        } else {
                          return [...prev, { barcode: crate.barcode, quantity: Math.max(0, value), size: crate.size }];
                        }
                      } else {
                        return prev.filter((c) => c.barcode !== crate.barcode);
                      }
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Exit confirmation modal */}
      <Modal
        title={t("save_progress")}
        open={exitModalVisible}
        onCancel={() => setExitModalVisible(false)}
        style={{ maxWidth: "85%", marginTop: "40%" }}
        footer={[
          <Button key="discard" danger onClick={handleDiscardProgress} disabled={isDiscarding}>
            {isDiscarding ? "Please wait..." : t("discard_progress")}
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveForLater}>
            {t("save_for_later")}
          </Button>,
        ]}
      >
        <p style={{ marginBlock: 15 }}>{t("do_you_want_to_save_progress")}</p>
      </Modal>

      {process.env.REACT_APP_ENVIRONMENT !== "production" && (
        <>
          <Button
            type="primary"
            shape="circle"
            icon={<QrcodeOutlined />}
            onClick={() => setMockScannerOpen(true)}
            style={{
              position: "fixed",
              right: 16,
              bottom: 16,
              zIndex: 2000,
              width: 56,
              height: 56,
              boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
            }}
          />

          <Modal
            title={t("crate_stock.mock_scanner")}
            open={mockScannerOpen}
            onCancel={() => setMockScannerOpen(false)}
            footer={null}
            maskClosable
            zIndex={3000}
          >
            <Tabs
              items={[
                {
                  key: "crates",
                  label: t("crate_stock.crates"),
                  children: (
                    <>
                      <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
                        <Input
                          value={mockCrateCodeInput}
                          onChange={(e) => setMockCrateCodeInput(e.target.value)}
                          placeholder={t("crate_stock.n_digit_crate_code_placeholder", {
                            digits: CRATE_STOCK_BARCODE_MAX_LENGTH,
                          })}
                          inputMode="numeric"
                          maxLength={CRATE_STOCK_BARCODE_MAX_LENGTH}
                          onPressEnter={() => addMockCode("crate")}
                        />
                        <Button icon={<PlusOutlined />} onClick={() => addMockCode("crate")} />
                      </Space.Compact>

                      <List
                        dataSource={mockCrateCodes}
                        locale={{ emptyText: t("crate_stock.no_crate_codes") }}
                        renderItem={(code) => (
                          <List.Item
                            actions={[
                              <Button
                                key="scan"
                                type="primary"
                                onClick={() => {
                                  setMockScannerOpen(false);
                                  simulateScan(code);
                                }}
                              >
                                {t("crate_stock.scan")}
                              </Button>,
                              <Button
                                key="delete"
                                icon={<DeleteOutlined />}
                                onClick={() => removeMockCode("crate", code)}
                              />,
                            ]}
                          >
                            <Space>
                              <Button onClick={() => simulateScan(code)} icon={<QrcodeOutlined />} />
                              <Text>{code}</Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </>
                  ),
                },
                {
                  key: "products",
                  label: t("crate_stock.products"),
                  children: (
                    <>
                      <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
                        <Input
                          value={mockProductCodeInput}
                          onChange={(e) => setMockProductCodeInput(e.target.value)}
                          placeholder={t("crate_stock.product_code_8_13_digits_placeholder")}
                          inputMode="numeric"
                          maxLength={13}
                          onPressEnter={() => addMockCode("product")}
                        />
                        <Button icon={<PlusOutlined />} onClick={() => addMockCode("product")} />
                      </Space.Compact>

                      <List
                        dataSource={mockProductCodes}
                        locale={{ emptyText: t("crate_stock.no_product_codes") }}
                        renderItem={(code) => (
                          <List.Item
                            actions={[
                              <Button
                                key="scan"
                                type="primary"
                                onClick={() => {
                                  setMockScannerOpen(false);
                                  simulateScan(code);
                                }}
                              >
                                {t("crate_stock.scan")}
                              </Button>,
                              <Button
                                key="delete"
                                icon={<DeleteOutlined />}
                                onClick={() => removeMockCode("product", code)}
                              />,
                            ]}
                          >
                            <Text>{code}</Text>
                          </List.Item>
                        )}
                      />
                    </>
                  ),
                },
              ]}
            />
          </Modal>
        </>
      )}

      {/* Barcode Scanner FAB */}
      {barcodeScannerEnabled && (
        <>
          <Button
            type="primary"
            shape="circle"
            icon={<QrcodeOutlined />}
            onClick={() => setBarcodeScannerOpen(true)}
            style={{
              position: "fixed",
              right: 16,
              bottom: process.env.REACT_APP_ENVIRONMENT !== "production" ? 88 : 16,
              zIndex: 2000,
              width: 56,
              height: 56,
              boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
            }}
          />

          <BarcodeScanner
            visible={barcodeScannerOpen}
            onClose={() => setBarcodeScannerOpen(false)}
            onScan={handleCameraBarcodeScan}
          />
        </>
      )}

      {/* Bundle Scanning Modal */}
      <ScanBundleItemsModal
        visible={scanBundleModalVisible}
        onCancel={() => setScanBundleModalVisible(false)}
        onSave={handleBundleScanSave}
        bundleProduct={selectedProduct}
        requestedBundles={Math.max(0, (selectedProduct?.requested || 0) - (tempFulfilled || 0))}
        products={products}
        crateStocks={crateStocks}
        findCrateStockByCrateId={findCrateStockByCrateId}
        getCrateStockItem={getCrateStockItem}
        getCrateShelfLifeStatus={getCrateShelfLifeStatus}
        handlePickingCrateScan={handlePickingCrateScan}
        applySoldToCrate={handleApplySoldToCrate}
        appendCratePickEvent={handleAppendCratePickEvent}
        LOG_ACTION_SOLD={LOG_ACTION_SOLD}
        order={order}
      />
    </div>
  );
};

export default OrderPickingDetail;
